import React, { useState, useEffect } from 'react';

const ColorSwatches = ({ product, api, onImageChange, images = [] }) => {
  const [selectedColor, setSelectedColor] = useState(null);
  const [availableColors, setAvailableColors] = useState([]);
  const [colorToMockup, setColorToMockup] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch available colors for this product's blueprint with timeout handling
  useEffect(() => {
    const fetchColors = async () => {
      if (!product.printifyProductId || !api) return;

      setIsLoading(true);
      setError(null);

      const startTime = Date.now();
      console.log(`🎨 Fetching colors for product ${product.printifyProductId}...`);

      try {
        const response = await api.getProductColors(product.printifyProductId);

        const elapsed = Date.now() - startTime;
        console.log(`🎨 Color fetch completed in ${elapsed}ms for product ${product.printifyProductId}`);

        if (response.success && response.colors) {
          setAvailableColors(response.colors);
          console.log(`🎨 Found ${response.colors.length} colors:`, response.colors.map(c => c.name));
        } else {
          console.log(`🎨 No colors found:`, response.error);
          setError(response.error || 'Colors not available');
        }
      } catch (err) {
        const elapsed = Date.now() - startTime;
        console.error(`🎨 Color fetch failed after ${elapsed}ms:`, err);
        setError('Colors temporarily unavailable');
      } finally {
        setIsLoading(false);
      }
    };

    // Add a small delay to batch requests and avoid overwhelming the API
    const timeoutId = setTimeout(fetchColors, 200);

    return () => clearTimeout(timeoutId);
  }, [product.printifyProductId, api]);

  // Build color → mockup mapping when colors and images are available
  useEffect(() => {
    if (!availableColors.length || !images.length) return;

    const mapping = {};

    for (const color of availableColors) {
      // Get all variant IDs for this color
      const colorVariantIds = color.variants.map(v => v.id);

      // Find mockup image that includes any of these variant IDs
      const matchingImage = images.find(img =>
        img.variant_ids &&
        img.variant_ids.some(vid => colorVariantIds.includes(vid))
      );

      if (matchingImage?.src) {
        mapping[color.name] = matchingImage.src;
      }
    }

    setColorToMockup(mapping);

    // Only log if we found mappings
    if (Object.keys(mapping).length > 0) {
      console.log(`🎨 Color-to-mockup mapping for product ${product.printifyProductId}:`, mapping);
    }
  }, [availableColors, images, product.printifyProductId]);

  const handleColorClick = (color) => {
    setSelectedColor(color.name);

    // Use color-specific mockup if available, fallback to default
    const mockupUrl = colorToMockup[color.name] || product.mockupUrl || product.mainImage;

    if (onImageChange) {
      onImageChange(mockupUrl, color);
    }
  };

  // Don't render if loading, error, or no colors available
  if (isLoading) {
    return (
      <div className="flex gap-1.5 animate-pulse">
        {[1, 2, 3].map(i => (
          <div key={i} className="w-6 h-6 rounded-full bg-slate-200"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-xs text-red-500">
        Colors unavailable
      </div>
    );
  }

  if (!availableColors || availableColors.length <= 1) {
    return null; // Don't show if only one color or no colors
  }

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-slate-500">Available Colors:</div>
      <div className="flex flex-wrap gap-2">
        {availableColors.map((color, index) => {
          const isSelected = selectedColor === color.name;
          // Check if color is close to white to add border
          const isWhite = color.hex.toLowerCase() === '#ffffff' || color.hex.toLowerCase() === '#fff';

          return (
            <div
              key={index}
              className={`group relative w-8 h-8 rounded-full cursor-pointer transition-all duration-200
                ${isSelected
                  ? 'ring-2 ring-blue-500 ring-offset-2 scale-110'
                  : 'hover:scale-110 hover:ring-2 hover:ring-slate-300 hover:ring-offset-1'
                }
              `}
              onClick={() => handleColorClick(color)}
              title={`${color.name} (${color.variants.length} size${color.variants.length > 1 ? 's' : ''})`}
            >
              <div
                className={`w-full h-full rounded-full shadow-sm ${isWhite ? 'border border-slate-200' : ''}`}
                style={{ backgroundColor: color.hex }}
              />

              {/* Tooltip on hover */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                {color.name}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ColorSwatches;
